import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BetsButton from '../Vets/Button';
import { Container, ModalContainer } from '../BaseModal';
import { formatUSD, formatVets } from '../../common/currency';
import { useLazyGetSwapQuoteQuery } from '../../redux/api/authorized/swap';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useSolana, toTokenAmount } from '../../hooks/useSolana';
import { selectTokenBySymbol } from '../../redux/slices/tokenSlice';
import BackButton from '../BackButton';

const ButtonsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  gap: 20px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.text.title};
  font-size: 40px;
  font-weight: 600;
  font-family: Manrope;
`;

const YourBalanceLabel = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;
  font-weight: 400;
  font-family: Manrope;
  margin-top: 10px;
  margin-bottom: -5px;
`;

const VetsBalance = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 36px;
  font-weight: 600;
  font-family: Manrope;
`;

const VetsTokenBalance = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 12px;
  font-weight: 400;
  font-family: Manrope;
  margin-bottom: 10px;
`;

const TwoSidesContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ConfirmSellVets = () => {
  const privyToken = useSelector(selectPrivyToken);
  const [vetsPrice, setVetsPrice] = useState(null);
  const [usdToReceive, setUsdToReceive] = useState(0);
  const [triggerGetSwapQuote] = useLazyGetSwapQuoteQuery();
  const { executeSwap, loading } = useSolana();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();

  const tokenAmount = searchParams.get('tokenAmount');

  const vetsToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'VETS'),
  );
  const vetsTokenAddress = vetsToken?.address;

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: vetsTokenAddress,
        outputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        amount: toTokenAmount(tokenAmount, 9),
      }).unwrap();
      setUsdToReceive(resp.quote.outAmount / 1000000);
    };
    if (privyToken && vetsTokenAddress) {
      fetchQuote();
    }
  }, [triggerGetSwapQuote, privyToken, tokenAmount, vetsTokenAddress]);

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        outputMint: vetsTokenAddress,
        amount: 1000000,
      }).unwrap();
      const price = formatVets(resp.quote.outAmount);
      setVetsPrice(price);
    };

    if (privyToken && vetsTokenAddress) {
      fetchQuote();
    }
  }, [triggerGetSwapQuote, privyToken, vetsTokenAddress]);

  const navigate = useNavigate();

  const handleSwap = async () => {
    const usdcMint = import.meta.env.VITE_SOLANA_USDC_MINT;
    const vetsMint = vetsTokenAddress;
    const resp = await executeSwap({
      inputMint: vetsMint,
      outputMint: usdcMint,
      amount: toTokenAmount(tokenAmount, 9),
    });
    if (resp) {
      toast.success('Sale Completed Successfully');
      navigate('/app/wallet');
    } else {
      toast.error('Error Processing Sale');
    }
  };

  return (
    <Container>
      <ModalContainer>
        <BackButton />
        <div>
          <Title>Sell VETS</Title>
        </div>
        <div>
          <YourBalanceLabel>Sell Amount</YourBalanceLabel>
          <VetsBalance>{tokenAmount}</VetsBalance>
          <VetsTokenBalance>
            VETS price: {formatUSD(vetsPrice)}
          </VetsTokenBalance>
        </div>
        <div>
          <TwoSidesContainer>
            <VetsTokenBalance>Sell Amount</VetsTokenBalance>
            <VetsTokenBalance>{tokenAmount} VETS</VetsTokenBalance>
          </TwoSidesContainer>
          <TwoSidesContainer>
            <VetsTokenBalance>Cause Fee (4%)</VetsTokenBalance>
            <VetsTokenBalance>{tokenAmount * 0.04} VETS</VetsTokenBalance>
          </TwoSidesContainer>
          <TwoSidesContainer>
            <VetsTokenBalance>You Will Receive</VetsTokenBalance>
            <VetsTokenBalance>
              {formatUSD(usdToReceive * 0.96)}
            </VetsTokenBalance>
          </TwoSidesContainer>
        </div>
        <ButtonsContainer>
          <BetsButton
            loading={loading}
            fullWidth
            disabled={!vetsTokenAddress}
            onClick={() => {
              handleSwap();
            }}
          >
            Confirm
          </BetsButton>
        </ButtonsContainer>
      </ModalContainer>
    </Container>
  );
};

export default ConfirmSellVets;
